import { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { PALS_BY_INDEX } from "@/lib/breeding";
import { WORK_META } from "@/data/breedingPals";
import { PalAvatar, ElementBadges } from "@/components/PalAvatar";
import { PalPicker } from "@/components/PalPicker";
import {
  X, Scale, Heart, Sword, Shield, Gauge, Wind, Coins, Star, Dna, Moon, Sun,
  Flame, Droplets, Sprout, Zap, Hammer, Hand, Axe, Pickaxe, FlaskConical, Snowflake, Truck, Wheat, Minus,
} from "lucide-react";

const WORK_ICONS = {
  kindling: Flame, watering: Droplets, planting: Sprout, electricity: Zap,
  handiwork: Hammer, gathering: Hand, lumbering: Axe, mining: Pickaxe,
  medicine: FlaskConical, cooling: Snowflake, transport: Truck, farming: Wheat,
};
const WORK_ORDER = ["kindling", "watering", "planting", "electricity", "handiwork", "gathering", "lumbering", "mining", "medicine", "cooling", "transport", "farming"];

const PalCompareContext = createContext(() => {});
export const usePalCompare = () => useContext(PalCompareContext);

function starsOf(rarity) {
  return Math.max(1, Math.min(5, Math.ceil((rarity || 0) / 2)));
}

// One numeric stat row: value A | label | value B, winner highlighted.
function StatRow({ icon: Icon, label, a, b, higherBetter = true, suffix = "" }) {
  const na = a == null ? null : Number(a);
  const nb = b == null ? null : Number(b);
  let aWin = false, bWin = false;
  if (na != null && nb != null && na !== nb) {
    if (higherBetter) { aWin = na > nb; bWin = nb > na; }
    else { aWin = na < nb; bWin = nb < na; }
  }
  const cell = (val, win) => (
    <div className={`flex-1 text-center py-2 rounded-md text-sm font-bold tabular-nums ${win ? "bg-[#22C55E1a] text-[#22C55E]" : "text-foreground"}`}>
      {val == null ? "–" : `${val}${suffix}`}
    </div>
  );
  return (
    <div className="flex items-center gap-2" data-testid={`compare-row-${label}`}>
      {cell(na, aWin)}
      <div className="w-24 sm:w-28 shrink-0 flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{label}</span>
      </div>
      {cell(nb, bWin)}
    </div>
  );
}

// Non-numeric info row (elements, size, activity...).
function InfoRow({ label, left, right }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 text-center py-1.5 text-xs text-slate-300">{left}</div>
      <div className="w-24 sm:w-28 shrink-0 text-center text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex-1 text-center py-1.5 text-xs text-slate-300">{right}</div>
    </div>
  );
}

function Column({ pal, idx, onPick, testid }) {
  return (
    <div className="min-w-0">
      <PalPicker value={idx} onChange={onPick} testid={testid} placeholder="Choisir un Pal…" />
      {pal && (
        <div className="mt-3 flex flex-col items-center text-center">
          <PalAvatar pal={pal} size={64} showElement />
          <div className="mt-2 font-head font-bold leading-tight truncate max-w-full">{pal.fr}</div>
          <div className="mt-1 flex flex-wrap justify-center gap-1"><ElementBadges elements={pal.elements} /></div>
          <div className="mt-1 inline-flex items-center gap-0.5 text-amber-400">
            {Array.from({ length: starsOf(pal.rarity) }).map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ init, onClose }) {
  const [a, setA] = useState(init?.[0] ?? null);
  const [b, setB] = useState(init?.[1] ?? null);
  const pa = a != null ? PALS_BY_INDEX[a] : null;
  const pb = b != null ? PALS_BY_INDEX[b] : null;
  const da = pa?.d || {};
  const db = pb?.d || {};
  const both = pa && pb;

  const workKeys = WORK_ORDER.filter((k) => (da.work && da.work[k]) || (db.work && db.work[k]));

  const activityLabel = (d) => d?.noct == null ? "–" : d.noct ? "Nocturne" : "Diurne";
  const wildLabel = (d) => (d?.wmin != null && d?.wmax != null) ? `${d.wmin}–${d.wmax}` : "–";

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-start justify-center p-4 pt-[6vh] bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="pal-compare-modal">
      <div className="relative w-full max-w-2xl rounded-2xl border border-border bg-[hsl(var(--surface))] shadow-2xl overflow-hidden fade-up max-h-[88vh] overflow-y-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center gap-2 p-4 border-b border-border bg-[hsl(var(--surface))]/95 backdrop-blur">
          <Scale className="w-5 h-5 text-primary" />
          <h3 className="font-head text-lg font-bold">Comparateur de Pals</h3>
          <button onClick={onClose} data-testid="pal-compare-close" className="ml-auto text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Column pal={pa} idx={a} onPick={setA} testid="compare-picker-a" />
            <Column pal={pb} idx={b} onPick={setB} testid="compare-picker-b" />
          </div>

          {!both ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="compare-empty">
              Choisis deux Pals pour voir la comparaison côte à côte.
            </div>
          ) : (
            <div className="space-y-4" data-testid="compare-table">
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2 text-center">Statistiques</div>
                <div className="space-y-1.5">
                  <StatRow icon={Dna} label="Puissance" a={pa.power} b={pb.power} />
                  <StatRow icon={Heart} label="PV" a={da.hp} b={db.hp} />
                  <StatRow icon={Sword} label="Attaque" a={da.atk} b={db.atk} />
                  <StatRow icon={Shield} label="Défense" a={da.def} b={db.def} />
                  <StatRow icon={Gauge} label="Monture" a={da.ride} b={db.ride} />
                  <StatRow icon={Wind} label="Course" a={da.run} b={db.run} />
                  <StatRow icon={Coins} label="Prix" a={da.price} b={db.price} higherBetter={false} />
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2 text-center">Infos</div>
                <div className="space-y-0.5 rounded-lg border border-border bg-black/20 p-1">
                  <InfoRow label="Rareté" left={`${pa.rarity}`} right={`${pb.rarity}`} />
                  <InfoRow label="Taille" left={da.size || "–"} right={db.size || "–"} />
                  <InfoRow label="Activité" left={activityLabel(da)} right={activityLabel(db)} />
                  <InfoRow label="Nv sauvage" left={wildLabel(da)} right={wildLabel(db)} />
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2 text-center">Compétences de travail</div>
                {workKeys.length === 0 ? (
                  <div className="text-center text-xs text-muted-foreground py-2">Aucune compétence de travail.</div>
                ) : (
                  <div className="space-y-1.5">
                    {workKeys.map((k) => {
                      const Icon = WORK_ICONS[k] || Hand;
                      const la = da.work?.[k] ?? 0;
                      const lb = db.work?.[k] ?? 0;
                      const aWin = la > lb, bWin = lb > la;
                      const cell = (lvl, win) => (
                        <div className={`flex-1 text-center py-1.5 rounded-md text-sm font-bold ${win ? "bg-[#22C55E1a] text-[#22C55E]" : lvl ? "text-foreground" : "text-muted-foreground"}`}>
                          {lvl ? `Niv ${lvl}` : <Minus className="w-3.5 h-3.5 inline" />}
                        </div>
                      );
                      return (
                        <div key={k} className="flex items-center gap-2" data-testid={`compare-work-${k}`}>
                          {cell(la, aWin)}
                          <div className="w-24 sm:w-28 shrink-0 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                            <Icon className="w-3.5 h-3.5 text-primary shrink-0" /> <span className="truncate">{WORK_META[k]}</span>
                          </div>
                          {cell(lb, bWin)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PalCompareProvider({ children }) {
  const [state, setState] = useState(null); // null | [a, b]
  const open = useCallback((idxA = null, idxB = null) => setState([idxA, idxB]), []);
  return (
    <PalCompareContext.Provider value={open}>
      {children}
      {state && <Modal init={state} onClose={() => setState(null)} />}
    </PalCompareContext.Provider>
  );
}
